import { stepBaseVictorsMedicalTugoLookup as expenseLookup } from './claimCodeMapper';
import { MEDICAL_EXPENSE_TYPES } from '../Tugo.BaseVictorsMedical/constants';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire() {
  return [];
}

// Generic function to map any type of expense
function mapExpensesByType(data, itemsKey, expenseLookupKey, condition = true) {
  if (!condition || !data[itemsKey] || data[itemsKey].length < 1) {
    return [];
  }

  // Check if lookup key exists in expense lookup
  if (!Object.prototype.hasOwnProperty.call(expenseLookup, expenseLookupKey)) {
    return [];
  }

  const claimCode = expenseLookup[expenseLookupKey].claimCode;
  const expenses = [];

  data[itemsKey].forEach((item) => {
    const expense = mapExpenseItem(item, data, claimCode);
    if (!!expense) {
      expenses.push(expense);
    }
  });

  return expenses;
}

function mapToExpenses(data) {
  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  const expenseConfigs = [
    // Doctor's Visit expenses
    {
      items: 'infoOutpatientPhysicianFeesItems',
      lookup: 'outpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true',
    },
    {
      items: 'infoFollowUpVisitItems',
      lookup: 'followUpVisit',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
    },
    {
      items: 'infoFollowUpFractureItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true',
    },

    // Diagnostic Tests expenses
    {
      items: 'infoDiagnosticTestsItems',
      lookup: 'diagnosticTests',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS),
    },

    // Prescription Drugs expenses
    {
      items: 'infoPrescriptionDrugsItems',
      lookup: 'prescriptionDrugs',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
    },

    // Ambulance Services expenses
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'groundAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'true',
    },
    {
      items: 'infoAirAmbulanceItems',
      lookup: 'airAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'true',
    },
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'groundAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'false',
    },

    // Hospitalization expenses
    {
      items: 'infoHospitalFeesItems',
      lookup: 'hospitalFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalInvoice === 'true',
    },
    {
      items: 'infoInpatientPhysicianFeesItems',
      lookup: 'inpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === 'true',
    },
    {
      items: 'infoXrayLabsDiagnosticsItems',
      lookup: 'xrayLabsDiagnostics',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === 'true',
    },
    {
      items: 'infoOtherHospitalizationItems',
      lookup: 'otherHospitalization',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === 'true',
    },

    // Essential Medical Appliances expenses
    {
      items: 'infoEssentialMedicalAppliancesItems',
      lookup: 'essentialMedicalAppliances',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
    },

    // Private Duty Nursing expenses
    {
      items: 'infoPrivateDutyNursingItems',
      lookup: 'privateDutyNursing',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
    },

    // Fracture Treatment expenses
    {
      items: 'infoFractureTreatmentItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT),
    },
  ];
  // Map all expenses using the generic function
  const allExpenses = expenseConfigs.flatMap((config) =>
    mapExpensesByType(data, config.items, config.lookup, config.condition)
  );

  return [...allExpenses];
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const selected =
    !!selectedBenefits && typeof selectedBenefits.toJS === 'function'
      ? selectedBenefits.toJS()
      : selectedBenefits || [];
  const claimCodeList = expenseLookup.claimCodeList;
  const list = [];
  if (selected.length > 0 && !selected.some((sel) => claimCodeList.includes(sel.claimCode))) {
    return list;
  }

  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];

  if (expensesSelected.length < 1 || claimTypes === undefined) {
    return list;
  }

  // Get all medical expenses
  const medicalExpenses = mapToExpenses(data);

  // Helper function to get claim code from lookup's associatedClaimCodes
  const getClaimCodeFromLookup = (lookupKey) => {
    const lookup = expenseLookup[lookupKey];
    return lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
      ? lookup.associatedClaimCodes[0]
      : null;
  };

  // Create mapping configuration by lookup keys (not hard-coded claim codes)
  // Note: Documents should ONLY be shown when specific questions are answered "Yes"
  // NOT when only the checkbox is selected
  const lookupKeyToConfig = {
    // Doctor's Visit - First Visit
    outpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: (claimCode) => {
        // Check if this is from Doctor's Visit (First Visit)
        const isDoctorVisitFirst = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true';
        // Check if this is from Hospitalization (Physician Invoice)
        const isHospitalizationPhysician = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === 'true';
        return isDoctorVisitFirst || isHospitalizationPhysician;
      },
      getDescription: () => {
        // If from Doctor's Visit First Visit, show "Physician fees"
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true') {
          return 'Physician fees';
        }
        // If from Hospitalization Physician Invoice, show "Physician fees"
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === 'true') {
          return 'Physician fees';
        }
        return MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT;
      },
    },
    // Doctor's Visit - Follow-up Visit
    followUpVisit: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
      getDescription: () => 'Follow-up visits',
    },
    // Fracture Treatment - Follow-up Fracture
    fractureTreatment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT],
      condition: () => {
        const isDoctorVisitFracture = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true';
        const isStandaloneFracture = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT);
        return isDoctorVisitFracture || isStandaloneFracture;
      },
      getDescription: () => 'Fracture treatment',
    },
    // Diagnostic Tests
    diagnosticTests: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS, MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: (claimCode) => {
        // Check if this is from Diagnostic Tests checkbox
        const isDiagnosticTests = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS);
        // Check if this is from Hospitalization (Lab/Diagnostic or Other Related)
        const isHospitalizationLab = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === 'true';
        const isHospitalizationOther = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === 'true';
        return isDiagnosticTests || isHospitalizationLab || isHospitalizationOther;
      },
      getDescription: () => {
        // If from Hospitalization, show "Diagnostic Tests"
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && 
            (data.hospitalizationLabDiagnostic === 'true' || data.hospitalizationOtherRelated === 'true')) {
          return 'Diagnostic Tests';
        }
        // Otherwise use the expense type
        return MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS;
      },
    },
    // Prescription Drugs
    prescriptionDrugs: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
      getDescription: () => MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS,
    },
    // Ambulance Services - Ground
    groundAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && (data.ambulanceGroundService === 'true' || data.ambulanceAirService === 'false'),
      getDescription: () => 'Ambulance services',
    },
    // Ambulance Services - Air
    airAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'true',
      getDescription: () => 'Emergency Air Transportation',
    },
    // Hospitalization - Hospital Fees
    hospitalFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalInvoice === 'true',
      getDescription: () => 'Hospitalization',
    },
    // Hospitalization - Inpatient Physician Fees (uses same VTC-PHYS as outpatientPhysicianFees)
    inpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === 'true',
      getDescription: () => 'Physician fees',
    },
    // Hospitalization - X-ray/Labs/Diagnostics (uses same VTC-DIAG as diagnosticTests)
    xrayLabsDiagnostics: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === 'true',
      getDescription: () => 'Diagnostic Tests',
    },
    // Hospitalization - Other Related (uses same VTC-DIAG as diagnosticTests)
    otherHospitalization: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === 'true',
      getDescription: () => 'Diagnostic Tests',
    },
    // Essential Medical Appliances
    essentialMedicalAppliances: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
      getDescription: () => MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES,
    },
    // Private Duty Nursing
    privateDutyNursing: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
      getDescription: () => MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING,
    },
  };

  // Build claimCodeToExpenseTypes dynamically from lookup keys
  const claimCodeToExpenseTypes = {};
  Object.keys(lookupKeyToConfig).forEach((lookupKey) => {
    const claimCode = getClaimCodeFromLookup(lookupKey);
    if (claimCode) {
      // If claim code already exists, merge the configs (for cases where multiple lookups share same claim code)
      if (claimCodeToExpenseTypes[claimCode]) {
        // Merge expense types
        claimCodeToExpenseTypes[claimCode].expenseTypes = [
          ...new Set([...claimCodeToExpenseTypes[claimCode].expenseTypes, ...lookupKeyToConfig[lookupKey].expenseTypes])
        ];
        // Merge conditions with OR logic
        const existingCondition = claimCodeToExpenseTypes[claimCode].condition;
        const newCondition = lookupKeyToConfig[lookupKey].condition;
        claimCodeToExpenseTypes[claimCode].condition = (claimCode) => 
          existingCondition(claimCode) || newCondition(claimCode);
        // Use the new description if it's more specific
        if (lookupKeyToConfig[lookupKey].getDescription) {
          claimCodeToExpenseTypes[claimCode].getDescription = lookupKeyToConfig[lookupKey].getDescription;
        }
      } else {
        claimCodeToExpenseTypes[claimCode] = { ...lookupKeyToConfig[lookupKey] };
      }
    }
  });

  // Map each claim type to its corresponding expenses
  claimTypes.forEach((item) => {
    const claimCodeConfig = claimCodeToExpenseTypes[item.claimCode];
    if (claimCodeConfig) {
      // Check if the specific condition is met (checkbox selected AND question answered "Yes")
      if (claimCodeConfig.condition(item.claimCode)) {
        // Find the matching expense type
        const matchingExpenseType = claimCodeConfig.expenseTypes.find((expenseType) =>
          expensesSelected.includes(expenseType)
        );
        if (matchingExpenseType) {
          // Get the appropriate description based on the specific condition
          const description = claimCodeConfig.getDescription 
            ? claimCodeConfig.getDescription() 
            : matchingExpenseType;
          const benefit = mapClaimExpensesLevelItem(
            item,
            description,
            medicalExpenses.filter((expense) => expense.claimTypeCode === item.claimCode)
          );
          list.push(benefit);
        }
      }
    }
  });

  return list;
}

function mapExpenseItem(item, itemDate, claimCode) {
  const selectedClaimForArray = (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor;
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
  };
}

function mapClaimExpensesLevelItem(claimType, description, expenses) {
  return {
    claimCode: claimType.claimCode,
    description,
    claimTypeId: claimType.claimTypeId,
    requiredDocuments: claimType.requiredDocuments,
    order: null,
    expenses: expenses || [],
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepBaseVictorsMedicalTugoDataMapper.js